import React from 'react';
import { Language } from '../types/kundali';
import { AdvancedLifeAnalysisResult, ProReasoningItem } from '../types/advancedLifeAnalysis';
import { BookOpen, X, ChevronRight, CheckCircle2, AlertTriangle, Sparkles, HelpCircle } from 'lucide-react';

interface ClassicalReasoningModalProps {
  isOpen: boolean;
  onClose: () => void;
  analysisResult: AdvancedLifeAnalysisResult;
  lang: Language;
}

export const ClassicalReasoningModal: React.FC<ClassicalReasoningModalProps> = ({
  isOpen,
  onClose,
  analysisResult,
  lang
}) => {
  if (!isOpen) return null;

  const isHi = lang === 'hi';
  const { proReasoningSteps, question, themeConfig } = analysisResult;

  return (
    <div
      id="classical-reasoning-modal-overlay"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#121E1C]/60 backdrop-blur-xs animate-fadeIn"
      onClick={onClose}
    >
      <div
        id="classical-reasoning-modal-content"
        className="bg-astro-bg border border-astro-border rounded-2xl max-w-3xl w-full max-h-[90vh] flex flex-col shadow-2xl overflow-hidden animate-scaleUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 sm:p-5 border-b border-astro-border flex items-center justify-between bg-astro-card">
          <div className="flex items-center gap-2.5">
            <BookOpen className="w-5 h-5 text-astro-accent" />
            <div>
              <h3 className="text-sm sm:text-base font-bold text-astro-text">
                {isHi ? 'यह निष्कर्ष क्यों? — शास्त्रीय पराशरीय तर्क प्रणाली' : 'Why This Conclusion? — Classical Parashari Reasoning'}
              </h3>
              <p className="text-xs text-astro-secondary-text">
                {isHi ? question.questionHi : question.questionEn}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-lg bg-astro-bg hover:bg-astro-secondary-surface text-astro-secondary-text hover:text-astro-text transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation cursor-pointer border border-astro-border"
            aria-label="Close reasoning dialog"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Body: 5 Step Classical Breakdown */}
        <div className="p-4 sm:p-6 overflow-y-auto space-y-5 divide-y divide-astro-border">
          {(proReasoningSteps || []).map((item: ProReasoningItem) => (
            <div key={item.step} className="pt-4 first:pt-0 space-y-2.5">
              <div className="flex items-center gap-2">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-astro-primary text-white text-xs font-bold font-mono">
                  {item.step}
                </span>
                <h4 className="text-sm font-bold text-astro-text">
                  {isHi ? item.titleHi : item.titleEn}
                </h4>
              </div>

              {/* Classical Sutra & Concept */}
              <div className="p-3 rounded-xl bg-astro-card border border-astro-border text-xs space-y-1">
                <span className="font-bold text-astro-primary block">
                  {isHi ? 'शास्त्रीय नियम / सूत्र:' : 'Classical Rule / Sutra:'}
                </span>
                <p className="text-astro-text italic font-serif leading-relaxed">
                  "{item.classicalRule}"
                </p>
              </div>

              {/* Calculation Evidence */}
              <div className="text-xs space-y-1">
                <span className="font-bold text-astro-text">
                  {isHi ? 'कुंडली प्रमाण व गणना:' : 'Chart Evidence & Calculation:'}
                </span>
                <p className="text-astro-secondary-text leading-relaxed">
                  {item.calculationEvidence}
                </p>
              </div>

              {/* Supporting & Conflicting Points */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs pt-1">
                <div className="p-2.5 rounded-lg bg-emerald-50/70 border border-emerald-200 space-y-1">
                  <span className="font-bold text-emerald-800 flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {isHi ? 'अनुकूल शास्त्रीय बिंदु' : 'Supporting Classical Points'}
                  </span>
                  <ul className="space-y-0.5 text-astro-text text-[11px]">
                    {item.supportingPoints.map((pt, i) => (
                      <li key={i}>• {pt}</li>
                    ))}
                  </ul>
                </div>

                <div className="p-2.5 rounded-lg bg-amber-50/70 border border-amber-200 space-y-1">
                  <span className="font-bold text-amber-800 flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    {isHi ? 'संभावित बाधा / चुनौती बिंदु' : 'Conflicting / Challenging Points'}
                  </span>
                  <ul className="space-y-0.5 text-astro-text text-[11px]">
                    {item.conflictingPoints.map((pt, i) => (
                      <li key={i}>• {pt}</li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Synthesis */}
              <div className="p-2.5 rounded-lg bg-astro-primary/5 border border-astro-primary/15 text-xs text-astro-primary font-medium">
                <strong>{isHi ? 'निष्कर्ष: ' : 'Synthesis: '}</strong>
                {item.synthesis}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-astro-border bg-astro-card flex items-center justify-between text-xs text-astro-secondary-text">
          <span>{isHi ? 'पराशरीय होरा शास्त्र एवं जाइमिनी पद्धति' : 'Parashari Hora Shastra & Jaimini Methodological Synthesis'}</span>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-astro-primary hover:bg-astro-secondary text-white font-bold transition-colors cursor-pointer shadow-xs"
          >
            {isHi ? 'बंद करें' : 'Close Reasoning'}
          </button>
        </div>
      </div>
    </div>
  );
};
